import React from 'react';
import { toast } from 'react-toastify';
import TableHeader from '../BaseComponents/TableHeader';
import FailedPaymentRow from './FailedPaymentRow';
import {
    FailedSubscriptionPaymentItem,
    getFailedSubscriptionPayments
} from '../../api/subscription';
import './FailedPaymentsTable.scss';

const headerColumns = [
    { title: 'Member', key: 'memberName', minWidth: '240px', width: '240px', flex: 1 },
    { title: 'Reason', key: 'failureReason', minWidth: '320px', width: '320px', flex: 1 },
    { title: 'Amount', key: 'amountDue', minWidth: '110px', width: '110px', flex: 1 },
    { title: 'Failed on', key: 'failedAt', minWidth: '120px', width: '120px', flex: 1 },
    { title: 'Status', key: 'subscriptionStatus', minWidth: '110px', width: '110px', flex: 1 },
    { title: 'Actions', key: 'actions', minWidth: '140px', width: '140px', flex: 1 }
];

const PAGE_SIZE = 20;

interface FailedPaymentsTableProps {
    onCountUpdate: (count: number) => void;
    reloadKey: number;
}

const FailedPaymentsTable: React.FC<FailedPaymentsTableProps> = ({ onCountUpdate, reloadKey }) => {
    const [data, setData] = React.useState<FailedSubscriptionPaymentItem[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [hasMore, setHasMore] = React.useState<boolean>(true);
    const offsetRef = React.useRef<number>(0);
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const scrollTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const fetchData = React.useCallback(async (isInitial: boolean = false) => {
        if (loading || (!hasMore && !isInitial)) return;

        setLoading(true);
        try {
            const currentOffset = isInitial ? 0 : offsetRef.current;
            const response = await getFailedSubscriptionPayments(PAGE_SIZE, currentOffset);
            const items = response.items || [];

            if (isInitial) {
                setData(items);
            } else {
                setData((prev) => [...prev, ...items]);
            }
            offsetRef.current = currentOffset + items.length;
            onCountUpdate(response.total_count || 0);
            setHasMore(items.length > 0 && offsetRef.current < response.total_count);
        } catch (error: any) {
            toast.error(
                error?.response?.data?.detail || error?.message || 'Failed to load failed payments'
            );
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    }, [loading, hasMore, onCountUpdate]);

    const handleScroll = React.useCallback(() => {
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        scrollTimeoutRef.current = setTimeout(() => {
            if (!scrollContainerRef.current || loading || !hasMore) return;

            const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
            const threshold = 100;
            const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;
            const isContentSmallerThanContainer = scrollHeight <= clientHeight + threshold;

            if (isNearBottom || isContentSmallerThanContainer) {
                fetchData();
            }
        }, 100);
    }, [loading, hasMore, fetchData]);

    const fetchDataRef = React.useRef(fetchData);
    fetchDataRef.current = fetchData;

    React.useEffect(() => {
        setData([]);
        setHasMore(true);
        offsetRef.current = 0;
        fetchDataRef.current(true);
    }, [reloadKey]);

    React.useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
            if (hasMore && data.length > 0) {
                setTimeout(() => handleScroll(), 100);
            }
        }
        return () => {
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', handleScroll);
            }
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, [handleScroll, hasMore, data.length]);

    return (
        <div className='scroll-container'>
            <div className='table-container failed-payments-table mt-0'>
                <TableHeader headerColumns={headerColumns} gap={'24px'} />
                <div className='table-body' ref={scrollContainerRef}>
                    {data.length === 0 && !loading ? (
                        <div className='empty-data' style={{ padding: '20px', textAlign: 'center' }}>
                            No failed subscription payments.
                        </div>
                    ) : (
                        data.map((item) => (
                            <FailedPaymentRow key={item.invoice_id} rowData={item} />
                        ))
                    )}
                    {loading && <div style={{ padding: '10px', textAlign: 'center' }}>Loading...</div>}
                </div>
            </div>
        </div>
    );
};

export default FailedPaymentsTable;
